import React, { useState, useEffect } from 'react';
import { generatePDF, generatePDFPreview } from '../utils/pdfGenerator';
import { squareAPI } from '../services/squareAPI';

const PrintModal = ({ items, onClose }) => {
  const [printFormat, setPrintFormat] = useState('swing-tags');
  const [startRow, setStartRow] = useState(1);
  const [startCol, setStartCol] = useState(1);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [quantities, setQuantities] = useState(null); // null = loading
  const [quantityError, setQuantityError] = useState(false);

  const isBatch = items.length > 1;
  const previewItem = items[0];

  // Fetch live inventory quantities when the modal opens
  useEffect(() => {
    const variationIds = items
      .flatMap(item => (item.variations || []).map(v => v.id))
      .filter(Boolean);

    squareAPI.getInventoryCounts(variationIds)
      .then(counts => setQuantities(counts))
      .catch(() => {
        setQuantityError(true);
        setQuantities({});
      });
  }, [items]);

  useEffect(() => {
    const preview = generatePDFPreview(previewItem, printFormat);
    setPdfPreview(preview);
  }, [printFormat, previewItem]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Merge fetched quantities into a variation; falls back to v.quantity for mock mode
  const enrichVariation = (v) => ({
    ...v,
    quantity: quantities !== null && v.id && v.id in quantities
      ? quantities[v.id]
      : (v.quantity || 0)
  });

  const handlePrint = async () => {
    setGenerating(true);
    try {
      let itemToPrint;
      if (!isBatch) {
        itemToPrint = {
          ...items[0],
          variations: (items[0].variations || []).map(enrichVariation)
        };
      } else {
        itemToPrint = {
          name: null,
          brand: null,
          variations: items.flatMap(item =>
            (item.variations || []).map(v => ({
              ...enrichVariation(v),
              name: item.name,
              brand: item.brand
            }))
          )
        };
      }
      const options = printFormat === 'avery-labels' ? { startRow, startCol } : {};
      await generatePDF(itemToPrint, printFormat, options);
      onClose();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const totalTags = items.reduce(
    (sum, item) => sum + (item.variations || []).reduce((vs, v) => vs + enrichVariation(v).quantity, 0),
    0
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Print Tags</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Quantity fetch error */}
          {quantityError && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Could not fetch live stock counts. Tag quantities may be inaccurate.
              </p>
            </div>
          )}

          {/* Batch info banner */}
          {isBatch && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                Printing tags for <strong>{items.length} items</strong> (
                {quantities === null
                  ? <span className="italic">counting…</span>
                  : <strong>{totalTags} total tags</strong>
                }).
                Preview shows the first item only.
              </p>
            </div>
          )}

          {/* Print Format Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Print Format</label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setPrintFormat('swing-tags')}
                className={`flex-1 px-6 py-4 rounded-lg border-2 text-center transition-all ${
                  printFormat === 'swing-tags'
                    ? 'bg-blue-50 border-blue-600 shadow-lg'
                    : 'bg-white border-gray-300 hover:border-blue-400'
                }`}
              >
                <div className="text-2xl mb-2">🏷️</div>
                <h3 className={`font-semibold ${printFormat === 'swing-tags' ? 'text-blue-700' : 'text-gray-800'}`}>
                  Swing Tags
                </h3>
                <p className="text-xs text-gray-500 mt-1">2 columns × 7 rows (14 tags/page)</p>
              </button>
              <button
                type="button"
                onClick={() => setPrintFormat('avery-labels')}
                className={`flex-1 px-6 py-4 rounded-lg border-2 text-center transition-all ${
                  printFormat === 'avery-labels'
                    ? 'bg-blue-50 border-blue-600 shadow-lg'
                    : 'bg-white border-gray-300 hover:border-blue-400'
                }`}
              >
                <div className="text-2xl mb-2">📋</div>
                <h3 className={`font-semibold ${printFormat === 'avery-labels' ? 'text-blue-700' : 'text-gray-800'}`}>
                  Avery 8927 Labels
                </h3>
                <p className="text-xs text-gray-500 mt-1">4 columns × 20 rows (80 labels/page)</p>
              </button>
            </div>
          </div>

          {/* Avery Starting Position */}
          {printFormat === 'avery-labels' && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Starting Position (for partially used sheets)
              </label>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">Start Row (1-20)</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={startRow}
                    onChange={(e) => setStartRow(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-600 mb-1">Start Column (1-4)</label>
                  <input
                    type="number"
                    min="1"
                    max="4"
                    value={startCol}
                    onChange={(e) => setStartCol(Math.min(4, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { setStartRow(1); setStartCol(1); }}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 mt-5"
                >
                  Reset
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {startRow === 1 && startCol === 1
                  ? 'Starting from the beginning (full sheet)'
                  : `Labels will start at Row ${startRow}, Column ${startCol} (skipping ${((startRow - 1) * 4) + (startCol - 1)} positions)`
                }
              </p>
            </div>
          )}

          {/* PDF Preview */}
          {pdfPreview && (
            <div className="mb-6 bg-gray-100 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Preview{isBatch ? ' (first item)' : ''}
              </h3>
              <div className="bg-white rounded shadow-lg overflow-hidden">
                <iframe
                  src={pdfPreview}
                  className="w-full"
                  style={{ height: '400px' }}
                  title="PDF Preview"
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={generating || quantities === null}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
            >
              {generating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintModal;
